import { cn } from './ui';

/**
 * Marca do weloze.lab — a divisão de P&D que desenvolve os sistemas internos.
 *
 * O ponto entre "weloze" e "lab" não é o caractere `.`: é um círculo geométrico
 * dimensionado em `em`, então ele escala junto com a tipografia e mantém a
 * mesma proporção em qualquer tamanho. Um ponto tipográfico ficaria pequeno
 * demais e variaria conforme a fonte carregada.
 *
 * O contraste de peso ("weloze" semibold, "lab" light) é o que faz as duas
 * palavras lerem como uma marca só, e não como duas palavras coladas.
 */

const SIZES = {
  sm: 'text-base',
  md: 'text-xl',
  lg: 'text-3xl',
  xl: 'text-4xl',
} as const;

export function Wordmark({
  className,
  tone = 'dark',
  size = 'md',
}: {
  className?: string;
  tone?: 'dark' | 'light';
  size?: keyof typeof SIZES;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-baseline whitespace-nowrap tracking-tight',
        SIZES[size],
        className,
      )}
    >
      <span className={cn('font-semibold', tone === 'light' ? 'text-white' : 'text-brand-700')}>
        weloze
      </span>
      <span
        aria-hidden
        className="mx-[0.11em] inline-block size-[0.23em] shrink-0 rounded-full bg-accent-400"
      />
      <span className={cn('font-light', tone === 'light' ? 'text-brand-100' : 'text-brand-500')}>
        lab
      </span>
    </span>
  );
}

/** Assinatura completa: marca + descritor da divisão. */
export function BrandLockup({
  className,
  tone = 'dark',
  size = 'lg',
}: {
  className?: string;
  tone?: 'dark' | 'light';
  size?: keyof typeof SIZES;
}) {
  // `items-start` para as duas linhas terem a largura do próprio conteúdo:
  // sem isso elas esticam até a largura do contêiner.
  return (
    <div className={cn('inline-flex flex-col items-start gap-1', className)}>
      <Wordmark tone={tone} size={size} />
      <span
        className={cn(
          'text-[0.65rem] font-medium uppercase tracking-[0.18em]',
          tone === 'light' ? 'text-brand-200/80' : 'text-slate-400',
        )}
      >
        Pesquisa &amp; Desenvolvimento
      </span>
    </div>
  );
}

/**
 * Símbolo da marca.
 *
 * Vem do arquivo `public/logo.svg` — o **mesmo** que o navegador usa como
 * favicon. Antes o símbolo era remontado em HTML/CSS com a letra "w"
 * tipográfica, e o resultado divergia do favicon, onde o "w" é traçado como
 * caminho vetorial. Um arquivo único elimina a possibilidade de divergirem.
 */
export function BrandMark({ className }: { className?: string }) {
  return (
    <img
      src="/logo.svg"
      alt=""
      aria-hidden
      width={36}
      height={36}
      draggable={false}
      className={cn('size-9 shrink-0 select-none', className)}
    />
  );
}
